import {ApolloClient, ApolloLink, gql, HttpLink, InMemoryCache} from "@apollo/client";
import {Defer20220824Handler} from "@apollo/client/incremental";
import {apiUrls} from "./utils.ts";
import "@apollo/client";
import {ErrorLink} from "@apollo/client/link/error";
import {CombinedGraphQLErrors, ServerError} from "@apollo/client/errors";

/**
 * @sources:
 *  - https://www.sitepoint.com/how-to-build-a-web-app-with-graphql-and-react/
 *  - https://www.apollographql.com/docs/react/get-started/
 *  - https://www.apollographql.com/docs/react/get-started/
 *  - https://www.apollographql.com/docs/react/networking/authentication/
 * @type {ApolloClient<unknown>}
 */
export const apollo11: ApolloClient = new ApolloClient({
    cache: new InMemoryCache({}),
    link: ApolloLink.from([
        new ErrorLink(({error, operation}) => {
            if (CombinedGraphQLErrors.is(error)) {
                // https://www.apollographql.com/docs/react/data/error-handling#network-errors
                error.errors.forEach(error => {
                    console.info(`[GraphQL error]: ${performance.now()} -> ${error.message}`, error);
                    // todo redo w/ new auth mechanism
                    if (error.extensions?.code == "UNAUTHORIZED") {
                        window.location.href = error.extensions?.redirect as string;
                    }
                });
            } else if (ServerError.is(error)) {
                // non-200 server error
                // todo best place to force-redirect user if cookie tampering is happening (or other specific error types)
                // todo https://www.apollographql.com/docs/react/data/error-handling/
                console.log(`[GraphQL network error]:`, error);
            }
        }),
        /**
         * Show operation name in Chrome DevTools Network Inspector :
         *  - https://github.com/apollographql/apollo-link/issues/264
         *  - https://chrome.google.com/webstore/detail/graphql-network-inspector/ndlbedplllcgconngcnfmkadhokfaaln?hl=en-GB
         */
        new HttpLink({
            uri: (operation) => `${apiUrls.gql()}/${operation.operationName}`,
            credentials: 'include',
        })
    ])
});

/**
 * todo use explicit type (though polluting) only to facilitate 'Find Usages' feature in WebStorm.
 * Note: The `query <Name>` is VERY important to properly identify requests in browser DevTools > Network.
 *  Do NOT use same `FetchAll` or `FetchSingle` for different queries, as they all appear w/ same name in Network.
 *
 * Note: For all listing-related data, prefer RTK w/ REST endpoints since they provide direct access to all
 *  fields, whereas w/ GQL we need to manually specify each field we want, which is tedious when we just want
 *  them anyways. Still, keep the GQL queries in case we need to fetch only a few precise fields (summary).
 */
export const gqlTemplates = {
    authenticatedSellerReport: gql`query AuthenticateSellerReport($token: String!) {
        authenticatedSellerReport(token: $token)
    }`,
    authenticatedBrokerPortal: gql`query AuthenticatedBrokerPortal {
        authenticatedBrokerPortal
    }`,

    googleAnalyticsStats: gql`query GoogleAnalyticsStats($mls: String!, $start: String, $end: String) {
        googleAnalyticsStats(mls: $mls, start: $start, end: $end) {
            totalViews
            cities {
                city
                views
            }
            sources {
                source
                sessions
            }
            events {
                event
                count
            }
        }
    }`,

    mcgillProject: gql`query McGillProject($mls: String!) {
        mcgillProject(mls: $mls)
    }`,

    websiteConfig: gql`query WebsiteConfig {
        websiteConfig {
            domain
            is_mcgill
            ev_only
            brokers
            offices
            restricted_offices
            property_types
            excluded_muns
            ptype_ordering {
                key
                order
            }
            default_grid_sort
            listing_target_self
            listing_index_policy
        }
    }`,

    brokerRedirects: gql`query BrokerRedirects {
        brokerRedirects {
            id
            site
        }
    }`,

    reactTexts: gql`query ReactTexts {
        reactTexts {
            key
            original
            custom_fr
            custom_en
        }
    }`,

    // get all meta fields for properly mapping column name (all-caps) to human-readable label
    metaFields: gql`query MetaFields {
        ctrMetaFields {
            name
            table
            label_fr
            label_en
        }
    }`,

    // used in admin module for editing those fields
    metaFields2: gql`query MetaFields {
        ctrMetaFields {
            name
            table
            label_fr
            label_en
            description_official
            position
            type
        }
    }`,
    staticUniqueKeys: gql`query StaticUniqueKeys {
        ctrStaticUniqueKeys {
            GENRES_PROPRIETES
            MUNICIPALITES
            QUARTIERS
            REGIONS
            SOUS_TYPE_CARACTERISTIQUES
            TYPE_CARACTERISTIQUES
            TYPES_BANNIERES
            VALEURS_FIXES
        }
    }`,
    dynamicUniqueKeys: gql`query DynamicUniqueKeys {
        ctrDynamicUniqueKeys {
            MEMBRES
            BUREAUX
            FIRMES
        }
    }`,


    /** DYNAMIC TABLES */

    // array of all listings w/ only the strict minimum attributes (for grid view) -- single listing uses REST
    inscriptions: gql`query Inscriptions($broker: String) {
        ctrInscriptions(broker: $broker) {
            NO_INSCRIPTION
            DATE_MODIF
            CODE_STATUT
            NOM_PLAN_EAU
            PRIX_DEMANDE
            PRIX_LOCATION_DEMANDE
            UM_PRIX_LOCATION_DEMANDE
            FREQUENCE_PRIX_LOCATION
            CATEGORIE_PROPRIETE
            GENRE_PROPRIETE
            NB_CHAMBRES
            NB_PIECES
            NB_UNITES_TOTAL
            NB_SALLES_BAINS
            NB_SALLES_EAU
            NO_CIVIQUE_DEBUT
            NO_CIVIQUE_FIN
            NOM_RUE_COMPLET
            MUN_CODE
            QUARTR_CODE
            APPARTEMENT
            SUPERFICIE_HABITABLE
            UM_SUPERFICIE_HABITABLE
            SUPERFICIE_TERRAIN
            UM_SUPERFICIE_TERRAIN
            ANNEE_CONTRUCTION
            CODE_ANNEE_CONSTRUCTION
            COURTIER_INSCRIPTEUR_1
            BUREAU_INSCRIPTEUR_1
            LONGITUDE
            LATITUDE
        }
    }`,
    // all thumbnail photos for all listings (1 img per listing for grid view)
    thumbnails: gql`query Thumbnails($mlsIds: [ID]) {
        ctrThumbnails(mlsIds: $mlsIds) {
            NO_INSCRIPTION
            SEQ
            PhotoURL
        }
    }`,
    // only get parking-related characteristics for all listings
    ctrParkingDetails: gql`query ParkingDetails($types: [String], $mlsIds: [ID]) {
        ctrSomeCaracteristiques(types: $types, mlsIds: $mlsIds) {
            NO_INSCRIPTION
            SCARAC_CODE
            NOMBRE
        }
    }`,

    /** STATIC TABLES */

    genresProprietes: gql`query GenresProprietes {
        ctrGenresProprietes {
            GENRE_PROPRIETE
            CATEGORIE_PROPRIETE
            DESCRIPTION_FRANCAISE
            DESCRIPTION_ANGLAISE
            CUSTOM_EN
            CUSTOM_FR
        }
    }`,
    regions: gql`query Regions {
        ctrRegions {
            CODE
            DESCRIPTION_FRANCAISE
            DESCRIPTION_ANGLAISE
            CUSTOM_EN
            CUSTOM_FR
        }
    }`,
    municipalites: gql`query Municipalites($codes: [String]) {
        ctrMunicipalites(codes: $codes) {
            CODE
            DESCRIPTION
            REGION_CODE
            CUSTOM_EN
            CUSTOM_FR
        }
    }`,
    quartiers: gql`query Quartiers($munCodes: [String]) {
        ctrQuartiers(munCodes: $munCodes) {
            CODE
            MUN_CODE
            DESCRIPTION_FRANCAISE
            DESCRIPTION_ANGLAISE
            CUSTOM_EN
            CUSTOM_FR
        }
    }`,
    typesCaracteristiques: gql`query TypesCaracteristiques {
        ctrTypesCaracteristiques {
            CODE
            DESCRIPTION_FRANCAISE
            DESCRIPTION_ANGLAISE
            CUSTOM_EN
            CUSTOM_FR
        }
    }`,
    sousTypesCaracteristiques: gql`query SousTypesCaracteristiques {
        ctrSousTypesCaracteristiques {
            CODE
            TCAR_CODE
            DESCRIPTION_FRANCAISE
            DESCRIPTION_ANGLAISE
            CUSTOM_EN
            CUSTOM_FR
        }
    }`,
    typesBannieres: gql`query TypesBannieres {
        ctrTypesBannieres {
            CODE
            NOM_ANGLAIS
            NOM_FRANCAIS
            CUSTOM_EN
            CUSTOM_FR
        }
    }`,
    valeursFixes: gql`query ValeursFixes {
        ctrValeursFixes {
            DOMAINE
            VALEUR
            DESCRIPTION_FRANCAISE
            DESCRIPTION_ANGLAISE
            CUSTOM_EN
            CUSTOM_FR
        }
    }`,

    /** BROKER-RELATED TABLES */

    firmes: gql`query Firmes {
        ctrFirmes {
            CODE
            COURTIER_CODE
            FIRME_PRINCIPALE
            NOM_LEGAL
            NO_CERTIFICAT
            TYPE_CERTIFICAT
        }
    }`,
    bureaux: gql`query Bureaux {
        ctrBureaux {
            CODE
            CODE_POSTAL
            COURRIEL
            FIRME_CODE
            MUNICIPALITE
            NOM_LEGAL
            NOM_RUE
            NO_CIVIQUE
            SITE_WEB
            TELEPHONE_1
            URL_LOGO_BUREAU
        }
    }`,
    membres: gql`query Membres {
        ctrMembres {
            CODE
            BUR_CODE
            COURRIEL
            PRENOM
            NOM
            NOM_SOCIETE
            PHOTO_URL
            SITE_WEB
            TELEPHONE_1
            TELEPHONE_2
            NO_CERTIFICAT
            TYPE_CERTIFICAT
        }
    }`,

    /** MUTATIONS */

    loginToBrokerPortal: gql`mutation LoginToBrokerPortal($password: String!) {
        loginToBrokerPortal(password: $password)
    }`,
    logout: gql`mutation Logout { logout }`,
    updateBrokerRedirect: gql`mutation UpdateBrokerRedirect($redir: BrokerRedirectInput!) {
        updateBrokerRedirect(redir: $redir) {
            id
            site
        }
    }`,
    updateReactText: gql`mutation UpdateReactText($text: ReactTextInput!) {
        updateReactText(text: $text) {
            key
            original
            custom_fr
            custom_en
        }
    }`,
    updateMetaField: gql`mutation UpdateMetaField($entry: CtrMetaFieldInput!) {
        updateCtrMetaField(entry: $entry)
    }`,
    updateStaticEntry: gql`mutation UpdateStaticEntry($entry: CtrStaticCustomInput!) {
        updateCtrStaticTable(entry: $entry)
    }`,
    updateDynamicEntry: gql`mutation UpdateDynamicEntry($entry: CtrDynamicCustomInput!) {
        updateCtrDynamicTable(entry: $entry)
    }`,

    updateWebsiteConfig: gql`mutation UpdateWebsiteConfig($cfg: WebsiteConfigInput!) {
        updateWebsiteConfig(cfg: $cfg) {
            domain
            is_mcgill
            ev_only
            brokers
            offices
            restricted_offices
            property_types
            excluded_muns
            ptype_ordering {
                key
                order
            }
            default_grid_sort
            listing_target_self
            listing_index_policy
        }
    }`,

    searchProperties: gql`query SearchProperties($term: String) {
        searchProperties(term: $term) {
            mls_id
            score
        }
    }`,

    setConfigOption: gql`mutation SetConfigOption($key: XConfigKeys!, $value: String!) {
        setConfigOption(key: $key, value: $value)
    }`,

    dummy: gql`mutation Dummy {
        __typename
    }`,
}

/*
Start: Inserted by Apollo Client 3->4 migration codemod.
Copy the contents of this block into a `.d.ts` file in your project to enable correct response types in your custom links.
If you do not use the `@defer` directive in your application, you can safely remove this block.
*/

declare module "@apollo/client" {
    export interface TypeOverrides extends Defer20220824Handler.TypeOverrides {
    }
}

/*
End: Inserted by Apollo Client 3->4 migration codemod.
*/
